<template>
  <div>
    <a-form class="ant-advanced-search-form" :form="companySearchForm" @submit.prevent="$emit('refresh-company-list')">
      <a-row :gutter="24">
        <a-col :span="8">
          <a-form-item label="Region">
            <a-select v-decorator="['region']" :allowClear="true" placeholder="Select region">
              <a-select-option value="sa-east-1">
                South America (sa-east-1)
              </a-select-option>
              <a-select-option value="us-west-2">
                USA (us-west-2)
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col :span="16" :style="{ textAlign: 'right' }">
          <a-form-item>
            <a-button type="primary" html-type="submit" icon="search">
              Search Companies
            </a-button>
            <a-button
              type="primary"
              @click="() => (this.createCompanyModalVisible = true)"
              :style="{ marginLeft: '8px' }"
              icon="plus"
            >
              Create New Company
            </a-button>
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>
    <a-modal
      v-model="createCompanyModalVisible"
      title="Create New Company"
      centered
      :afterClose="() => this.createCompanyForm.resetFields()"
    >
      <a-form layout="vertical" id="createCompanyForm" :form="createCompanyForm" @submit="createCompany">
        <a-form-item label="Company Name">
          <a-input
            v-decorator="[
              'name',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a name!'
                  }
                ]
              }
            ]"
          />
        </a-form-item>
        <a-form-item label="Region">
          <a-select
            v-decorator="[
              'region',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select a region!'
                  }
                ]
              }
            ]"
            :allowClear="true"
            placeholder="Select region"
          >
            <a-select-option value="sa-east-1">
              South America (sa-east-1)
            </a-select-option>
            <a-select-option value="us-west-2">
              USA (us-west-2)
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <span slot="label"
            >Has Multiple Transformers
            <a-tooltip
              title="Select Yes if the company has multiple transformers under one depot - used to determine display labels and search options"
            >
              <a-icon type="question-circle-o" />
            </a-tooltip>
          </span>
          <a-radio-group
            v-decorator="[
              'hasTransformer',
              { initialValue: 'false' },
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select at least one value!'
                  }
                ]
              }
            ]"
          >
            <a-radio value="true">
              Yes
            </a-radio>
            <a-radio value="false">
              No
            </a-radio>
          </a-radio-group>
        </a-form-item>
      </a-form>
      <template slot="footer">
        <a-button
          form="createCompanyForm"
          type="primary"
          icon="plus"
          html-type="submit"
          :loading="createCompanyFormButtonLoading"
        >
          Create
        </a-button>
      </template>
    </a-modal>
    <br />
    <div class="search-result-list">
      <a-table
        :columns="columns"
        :dataSource="handleCompanyData"
        :scroll="{ x: 1500, y: 1000 }"
        :loading="this.companyDataLoading"
        :rowKey="record => record.company"
        :pagination="{
          defaultPageSize: 10,
          showSizeChanger: true
        }"
      >
        <div slot="footer">
          <span style="float: left; display: block;">
            {{ (masterCompanyListObject ? masterCompanyListObject.listOfCompanies.length : 0) + " Companies Found" }}
          </span>
          <span style="text-align: right; display: block;">
            {{
              "Revision Date: " +
                (masterCompanyListObject
                  ? this.$moment(masterCompanyListObject.revision)
                      .local()
                      .format("YYYY-MM-DD HH:mm")
                  : "N/A") +
                " | Last Updated By: " +
                (masterCompanyListObject ? masterCompanyListObject.updatedBy : "N/A")
            }}
          </span>
        </div>
        <span slot="actions" slot-scope="record">
          <a @click="showDeleteConfirm(record)">Delete</a>
        </span>
      </a-table>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  data() {
    return {
      companySearchForm: this.$form.createForm(this),
      createCompanyForm: this.$form.createForm(this),
      createCompanyModalVisible: false,
      createCompanyFormButtonLoading: false,
      deleteCompanyButtonLoading: false
    };
  },

  props: ["masterCompanyListObject", "AWS", "companyDataLoading", "cognitoidentityserviceprovider"],

  computed: {
    ...mapState(["UserName", "PoolData"]),

    handleCompanyData() {
      if (this.masterCompanyListObject) {
        const data = [...this.masterCompanyListObject.listOfCompanies];
        data.forEach(company => {
          if (company.lastUpdated) {
            company.lastUpdated = this.$moment(company.lastUpdated)
              .local()
              .format("YYYY-MM-DD HH:mm");
          } else {
            company.lastUpdated = "N/A";
          }
        });
        data.sort((recordOne, recordTwo) => {
          return recordOne.company.localeCompare(recordTwo.company);
        });
        // finally, filter the data if a region is selected.
        if (this.companySearchForm.getFieldValue("region")) {
          const filteredData = data.filter(company => {
            return company.region === this.companySearchForm.getFieldValue("region");
          });
          return filteredData;
        }
        return data;
      } else {
        return [];
      }
    },

    columns() {
      const columns = [
        {
          dataIndex: "company",
          title: "Company Name",
          align: "center"
        },
        {
          dataIndex: "depots",
          title: "Location(s)",
          customRender: depotList => {
            if (depotList && depotList.length > 0) {
              const originalList = [...depotList];
              return originalList.sort().join(", ");
            } else {
              return "N/A";
            }
          },
          align: "center",
          width: 400
        },
        {
          dataIndex: "fleets",
          title: "Fleet(s)",
          customRender: fleetList => {
            if (fleetList && fleetList.length > 0) {
              const originalList = [...fleetList];
              return originalList.sort().join(", ");
            } else {
              return "N/A";
            }
          },
          align: "center",
          width: 200
        },
        {
          dataIndex: "region",
          title: "Region",
          align: "center"
        },
        {
          dataIndex: "hasTransformer",
          title: "Has Multiple Tranformers",
          customRender: hasTransformer => {
            if (hasTransformer) {
              return "Yes";
            } else {
              return "No";
            }
          },
          align: "center"
        },
        {
          title: "Last Updated",
          dataIndex: "lastUpdated",
          align: "center"
        },
        {
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center"
        }
      ];
      return columns;
    }
  },

  methods: {
    createCompany(event) {
      event.preventDefault();
      this.createCompanyForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.createCompanyFormButtonLoading = true;
          var params = {
            objectType: "MCL",
            operation: "add",
            company: fieldValues.name,
            revision: new Date().toISOString(),
            username: this.UserName,
            region: fieldValues.region,
            hasTransformer: fieldValues.hasTransformer
          };
          await this.sendRequest(params, {}, "get");
          this.createCompanyFormButtonLoading = false;
          this.createCompanyForm.resetFields();
          this.createCompanyModalVisible = false;
          this.$message.success("Successfully created new company!");
          this.$emit("refresh-company-list");
        } catch (error) {
          this.createCompanyFormButtonLoading = false;
          this.$message.error(error);
          this.createCompanyForm.resetFields();
          this.createCompanyModalVisible = false;
          this.$emit("refresh-company-list");
          return;
        }
      });
    },

    async deleteCompany(record) {
      return new Promise(async resolve => {
        var promiseArray = [];
        // create promises to delete the fleets and depots under the company.
        if (record.depots) {
          let deleteParams = {
            objectType: "DPO",
            operation: "delete",
            company: record.company,
            revision: new Date().toISOString(),
            username: this.UserName,
            region: record.region
          };
          promiseArray.push(this.sendRequest(deleteParams, {}, "get"));
        }
        if (record.fleets) {
          let deleteParams = {
            objectType: "FLT",
            operation: "delete",
            company: record.company,
            revision: new Date().toISOString(),
            username: this.UserName,
            region: record.region
          };
          promiseArray.push(this.sendRequest(deleteParams, {}, "get"));
        }
        // create promise to delete the company object from the MCL.
        var deleteCompanyParams = {
          objectType: "MCL",
          operation: "delete",
          company: record.company,
          revision: new Date().toISOString(),
          username: this.UserName,
          region: record.region
        };
        promiseArray.push(this.sendRequest(deleteCompanyParams, {}, "get"));
        // finally, get all the users and create promises to delete the company from the authorized companies list.
        var listUsersParams = {
          UserPoolId: this.PoolData.UserPoolId,
          AttributesToGet: ["custom:authorizedCompanies"]
        };
        this.cognitoidentityserviceprovider.listUsers(listUsersParams, (error, data) => {
          if (error) {
            this.$message.error("Error retrieving users from User Pool.");
            resolve();
          } else {
            // once user data is returned, create a promise for each user who has the company in their authorized companies list.
            const userData = data.Users;
            userData.map(user => {
              let companyList = JSON.parse(user.Attributes[0].Value);
              companyList.forEach(companyObject => {
                if (companyObject.company === record.company) {
                  const updatedCompanyList = companyList.filter(
                    companyObject => companyObject.company !== record.company
                  );
                  let updateParams = {
                    UserPoolId: this.PoolData.UserPoolId,
                    Username: user.Username,
                    UserAttributes: [
                      {
                        Name: "custom:authorizedCompanies",
                        Value: JSON.stringify(updatedCompanyList)
                      }
                    ]
                  };
                  // create a promise to update the authorizedCompanies user attribute.
                  var updateUserAttributesPromise = this.cognitoidentityserviceprovider
                    .adminUpdateUserAttributes(updateParams)
                    .promise();
                  promiseArray.push(updateUserAttributesPromise);
                }
              });
            });
          }
        });
        try {
          // wait for all the promises to return.
          await Promise.all(promiseArray);
          this.$message.success("Successfully deleted company!");
          this.$emit("refresh-all-data");
          resolve();
        } catch (error) {
          this.$error({
            title: "Error Deleting Company",
            content:
              "Error Message - " +
              error.message +
              ". Please contact the Support team in order to delete items manually if necessary."
          });
          this.$emit("refresh-all-data");
          resolve();
        }
      });
    },

    showDeleteConfirm(record) {
      this.$confirm({
        title: "Are you sure you want to delete this company?",
        content:
          "Deleting a company will permanently remove it from users' authorized companies list and all its depots and fleets from the database.",
        okText: "Yes",
        okType: "danger",
        okProps: {
          loading: this.deleteCompanyButtonLoading
        },
        cancelText: "No",
        onOk: () => {
          return this.deleteCompany(record);
        }
      });
    },

    sendRequest(params, body, requestType) {
      // before making an API request, check to make sure user has access to perform the action / credentials need to be refreshed.
      return new Promise((resolve, reject) => {
        this.AWS.config.credentials.get(async credentialError => {
          if (credentialError) {
            if (credentialError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(credentialError.message);
            }
          } else {
            try {
              var aws4 = require("aws4");
              let paramsUrl = this.createParamsUrl(params);
              switch (requestType) {
                case "get": {
                  let request = {
                    host: "v9ovun9ana.execute-api.us-west-2.amazonaws.com",
                    method: "GET",
                    url: "https://v9ovun9ana.execute-api.us-west-2.amazonaws.com/prod/configure?" + paramsUrl,
                    path: "/prod/configure?" + paramsUrl,
                    service: "execute-api"
                  };
                  let signedRequest = aws4.sign(request, {
                    secretAccessKey: this.AWS.config.credentials.secretAccessKey,
                    accessKeyId: this.AWS.config.credentials.accessKeyId,
                    sessionToken: this.AWS.config.credentials.sessionToken
                  });
                  // delete unsafe headers from the signed request.
                  delete signedRequest.headers["Host"];
                  delete signedRequest.headers["Content-Length"];

                  let response = await this.$axios(signedRequest);
                  let items = response.data.Items;
                  resolve(items);
                  break;
                }
                case "post": {
                  let request = {
                    host: "v9ovun9ana.execute-api.us-west-2.amazonaws.com",
                    method: "POST",
                    url: "https://v9ovun9ana.execute-api.us-west-2.amazonaws.com/prod/configure?" + paramsUrl,
                    path: "/prod/configure?" + paramsUrl,
                    service: "execute-api",
                    data: body,
                    body: JSON.stringify(body),
                    headers: {
                      "content-type": "application/json"
                    }
                  };
                  let signedRequest = aws4.sign(request, {
                    secretAccessKey: this.AWS.config.credentials.secretAccessKey,
                    accessKeyId: this.AWS.config.credentials.accessKeyId,
                    sessionToken: this.AWS.config.credentials.sessionToken
                  });
                  // delete unsafe headers from the signed request.
                  delete signedRequest.headers["Host"];
                  delete signedRequest.headers["Content-Length"];

                  let response = await this.$axios(signedRequest);
                  let responseData = response.data;
                  resolve(responseData);
                  break;
                }
                default:
                  break;
              }
            } catch (error) {
              // if no response is available, means it is a network error.
              if (!error.response) {
                reject(new Error("Network Error. Please try again!"));
              } else {
                let response = error.response;
                if (response.status == 403) {
                  reject(
                    "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                  );
                } else {
                  reject(response.data.message);
                }
              }
            }
          }
        });
      });
    },

    createParamsUrl(paramsObject) {
      var paramsUrl = "";
      var counter = 0;
      var totalCount = Object.keys(paramsObject).length;
      for (var key in paramsObject) {
        if (paramsObject.hasOwnProperty(key)) {
          paramsUrl += key + "=" + paramsObject[key];
          if (counter != totalCount - 1) {
            paramsUrl += "&";
          }
          counter += 1;
        }
      }
      return paramsUrl.replace("#", "%23");
    }
  }
};
</script>

<style></style>
